import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, LinearProgress, Tooltip, Typography } from '@mui/material';
import {
  AutorenewRounded,
  CheckCircleOutline,
  ErrorOutline,
  OpenInNewRounded,
  VisibilityOutlined,
} from '@mui/icons-material';
import { InkyMascot, InkyWorking } from '../brand';
import { listGeneratedFields, summarizeFieldStatus } from '../documents/documentMeta';

const VISIBLE_FIELDS = 12;

/**
 * Per-field status chips for a generated document.
 * @param {object} props - Props.
 * @param {Array<{id: string, status: string, label: string}>} props.fields - Fields.
 * @returns {JSX.Element} Chip list.
 */
function FieldStatusList({ fields }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? fields : fields.slice(0, VISIBLE_FIELDS);
  return (
    <Box component="ul" aria-label="Sections" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {shown.map((field) => (
        <Box component="li" key={field.id} sx={{ maxWidth: '100%' }}>
          <Tooltip title={field.heading && field.heading !== field.label ? `${field.heading}: ${field.label}` : field.label}>
            <Chip
              size="small"
              color={field.status === 'needs_input' ? 'warning' : 'success'}
              icon={field.status === 'needs_input' ? <ErrorOutline /> : <CheckCircleOutline />}
              label={field.status === 'needs_input' ? `Needs input: ${field.label}` : field.label}
              sx={{ maxWidth: '100%' }}
            />
          </Tooltip>
        </Box>
      ))}
      {fields.length > VISIBLE_FIELDS && (
        <Box component="li">
          <Button size="small" onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Show fewer' : `Show all ${fields.length}`}
          </Button>
        </Box>
      )}
    </Box>
  );
}

/**
 * Live status of a queued generation and, once done, the result with per-field status,
 * open/preview actions and "Regenerate" (same inputs, next variant).
 * @param {object} props - Props.
 * @param {object|null} props.request - ai_requests row (status, progress, progress_message, error_message, result_data).
 * @param {object|null} props.document - Generated document row (content, trust_metadata).
 * @param {string} props.loadError - Error loading the generated document.
 * @param {string} props.documentName - Name the document was requested with.
 * @param {number} props.variant - Variant of this run (0 = first).
 * @param {Array<{id: string, instruction: string}>} props.templateFields - Template field definitions (labels).
 * @param {boolean} props.busy - A regenerate request is being created.
 * @param {function(object): void} props.onOpen - Open the generated document in the editor.
 * @param {function(object): void} props.onPreview - Preview the generated document.
 * @param {function(): void} props.onRegenerate - Generate the next variant.
 * @param {function(): void} props.onRetry - Retry the same variant after a failure.
 * @param {function(): void} props.onEditInputs - Return to the wizard with the same selections.
 * @param {function(): void} props.onReset - Start over.
 * @param {function(): void} props.onViewProject - Open the project's Documents tab.
 * @returns {JSX.Element} Status panel.
 */
export default function GenerationResult({
  request,
  document,
  loadError,
  documentName,
  variant,
  templateFields,
  busy,
  onOpen,
  onPreview,
  onRegenerate,
  onRetry,
  onEditInputs,
  onReset,
  onViewProject,
}) {
  const status = request?.status || 'pending';
  const fields = useMemo(
    () => (document?.content
      ? listGeneratedFields(document.content, {
        perField: document.trust_metadata?.manifest?.perField || [],
        templateFields,
      })
      : []),
    [document, templateFields]
  );
  const summary = useMemo(
    () => (document ? summarizeFieldStatus({ content: document.content, trustMetadata: document.trust_metadata }) : null),
    [document]
  );

  if (status === 'failed' || status === 'cancelled') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <InkyMascot size={48} mood="error" decorative />
          <Box>
            <Typography component="h2" sx={{ fontSize: 16, fontWeight: 600 }}>
              {status === 'cancelled' ? 'Generation was cancelled' : 'Generation failed'}
            </Typography>
            <Typography variant="body2" color="text.secondary">{documentName}</Typography>
          </Box>
        </Box>
        {status === 'failed' && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {request?.error_message || 'Something went wrong while generating the document.'}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="contained" onClick={onRetry} disabled={busy}>Try again</Button>
          <Button variant="outlined" onClick={onEditInputs} disabled={busy}>Change inputs</Button>
        </Box>
      </Box>
    );
  }

  if (status !== 'completed') {
    const progress = Math.max(5, Math.min(100, Number(request?.progress) || 5));
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <InkyWorking label="Inky is writing your document" mood="writing" size={40} />
        <Typography component="h2" sx={{ fontSize: 16, fontWeight: 600, overflowWrap: 'anywhere' }}>{documentName}</Typography>
        <LinearProgress variant="determinate" value={progress} aria-label="Generation progress" sx={{ height: 6, borderRadius: 3 }} />
        <Typography variant="body2" color="text.secondary" aria-live="polite">
          {request?.progress_message || 'Queued for generation…'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          You can leave this page. The jobs panel in the header tracks progress and the document is saved to the project when it is done.
        </Typography>
        <Box>
          <Button variant="outlined" onClick={onViewProject}>Go to project</Button>
        </Box>
      </Box>
    );
  }

  const ready = Boolean(document);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <InkyMascot size={48} mood="happy" decorative />
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" sx={{ fontSize: 16, fontWeight: 600 }}>Document ready</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            {document?.name || documentName}
            {variant > 0 ? ` · variant ${variant + 1}` : ''}
          </Typography>
        </Box>
      </Box>

      {loadError && <Alert severity="warning">{loadError}</Alert>}

      {summary ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {summary.needsInput > 0
              ? `${summary.filled} of ${summary.total} sections filled · ${summary.needsInput} need your input`
              : `All ${summary.total} sections filled`}
          </Typography>
          {fields.length > 0 && <FieldStatusList fields={fields} />}
          {summary.unresolvedVariables?.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {`Project details that could not be filled in: ${summary.unresolvedVariables.join(', ')}. Add them to the project or edit them in the document.`}
            </Typography>
          )}
          {summary.needsInput > 0 && (
            <Typography variant="caption" color="text.secondary">
              Sections marked "Needs input" had no supporting evidence. Open the document and use "Fill this section", or add requirements and regenerate.
            </Typography>
          )}
        </Box>
      ) : ready ? (
        <Typography variant="body2" color="text.secondary">
          Section status appears for templates that use AI fields.
        </Typography>
      ) : null}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button
          variant="contained"
          startIcon={<OpenInNewRounded />}
          onClick={() => onOpen(document || { id: request?.result_data?.documentId, name: documentName })}
          disabled={!document && !request?.result_data?.documentId}
        >
          Open document
        </Button>
        <Button variant="outlined" startIcon={<VisibilityOutlined />} onClick={() => onPreview(document)} disabled={!ready}>
          Preview
        </Button>
        <Tooltip title="Same inputs, a different version. The current document is kept.">
          <span>
            <Button variant="outlined" startIcon={<AutorenewRounded />} onClick={onRegenerate} disabled={busy}>
              Regenerate
            </Button>
          </span>
        </Tooltip>
        <Button onClick={onReset} disabled={busy}>Generate another</Button>
      </Box>
    </Box>
  );
}
